import {type ReactNode} from 'react';
import {Flag} from 'lucide-react';
import {Button} from '@kroll-resolver-crisp/ui-components-react';
import {type ImageMatchTier, imageMatchTierLabels} from '@/api/jobs';
import {FlagIcon, useFlagLabel} from '@/components/flags/Flag';
import {formatRdSignalValue} from '@/components/investigations/RdSignalFilter';
import {type FilterCategory, TIER_ORDER} from '@/components/investigations/InvestigationFilters';
import {activeItemFilters} from '@/components/items/ItemFiltersMenu';
import {type FlagFilter} from '@/lib/flagFilter';
import {formatRdSignalName} from '@/lib/rdSignals';
import {type SearchQuery} from '@/lib/searchQuery';
import {DEFAULT_ITEM_FILTERS, type ItemFilterState} from '@/store/uiStore';

export type ActiveFilterChip = {
  readonly id: string;
  readonly category: FilterCategory | null;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly onRemove: () => void;
};

export function useActiveFilterChips({
  query,
  onQueryChange,
  itemFilters,
  onItemFiltersChange,
  flags,
  onFlagsChange,
  excludedTiers,
  onExcludedTiersChange,
  smartFilter
}: {
  readonly query: string;
  readonly onQueryChange: (next: string) => void;
  readonly itemFilters: ItemFilterState;
  readonly onItemFiltersChange: (patch: Partial<ItemFilterState>) => void;
  readonly flags: readonly FlagFilter[];
  readonly onFlagsChange: (next: FlagFilter[]) => void;
  // Empty when match strength does not apply, so a stale exclusion never shows as a filter.
  readonly excludedTiers: ReadonlySet<ImageMatchTier>;
  readonly onExcludedTiersChange: (next: ReadonlySet<ImageMatchTier>) => void;
  readonly smartFilter: {
    readonly active: boolean;
    readonly query: SearchQuery;
    readonly includesEnabled: boolean;
    readonly onDisable: () => void;
  };
}) {
  const labelOf = useFlagLabel();
  const chips: ActiveFilterChip[] = [];

  if (query.trim() !== '') {
    chips.push({
      id: 'query',
      category: null,
      label: `Text: ${query.trim()}`,
      onRemove() {
        onQueryChange('');
      }
    });
  }

  if (smartFilter.active) {
    const terms = [
      ...smartFilter.query.exclude.map((term) => `−${term}`),
      ...(smartFilter.includesEnabled ? smartFilter.query.include : [])
    ];
    chips.push({
      id: 'smart',
      category: 'terms',
      label: `Smart filter: ${terms.join(' ')}`,
      onRemove: smartFilter.onDisable
    });
  }

  for (const tier of TIER_ORDER.filter((candidate) => excludedTiers.has(candidate))) {
    chips.push({
      id: `tier-${tier}`,
      category: 'content',
      label: `Hiding ${imageMatchTierLabels[tier]} matches`,
      onRemove() {
        const next = new Set(excludedTiers);
        next.delete(tier);
        onExcludedTiersChange(next);
      }
    });
  }

  for (const flag of flags) {
    chips.push({
      id: `flag-${flag}`,
      category: 'content',
      label: flag === 'any' ? 'Any flag' : labelOf(flag),
      icon: flag === 'any' ? <Flag /> : <FlagIcon color={flag} />,
      onRemove() {
        onFlagsChange(flags.filter((current) => current !== flag));
      }
    });
  }

  for (const filter of activeItemFilters(itemFilters)) {
    chips.push({
      id: filter.id,
      category: 'video',
      label: filter.label,
      onRemove() {
        onItemFiltersChange(filter.patch);
      }
    });
  }

  for (const selection of itemFilters.rdFilters) {
    const name = formatRdSignalName(selection.signal);
    chips.push({
      id: `rd-${selection.signal}`,
      category: 'risk',
      label:
        selection.values.length > 0
          ? `${name}: ${selection.values.map((value) => formatRdSignalValue(value, selection.signal)).join(', ')}`
          : name,
      onRemove() {
        onItemFiltersChange({
          rdFilters: itemFilters.rdFilters.filter((current) => current.signal !== selection.signal)
        });
      }
    });
  }

  const categoryCounts: Record<FilterCategory, number> = {risk: 0, terms: 0, content: 0, video: 0};
  for (const chip of chips) {
    if (chip.category !== null) {
      categoryCounts[chip.category] += 1;
    }
  }

  const clearAll = () => {
    onQueryChange('');
    onFlagsChange([]);
    onExcludedTiersChange(new Set());
    onItemFiltersChange({...DEFAULT_ITEM_FILTERS, sortOrder: itemFilters.sortOrder});
    if (smartFilter.active) {
      smartFilter.onDisable();
    }
  };

  return {chips, categoryCounts, clearAll};
}

export function ActiveFilterChips({
  chips,
  onClearAll
}: {
  readonly chips: readonly ActiveFilterChip[];
  readonly onClearAll: () => void;
}) {
  if (chips.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2 px-5 pt-2"
      role="group"
      aria-label="Active filters">
      {chips.map((chip) => (
        <Button
          key={chip.id}
          variant="secondary"
          size="xs"
          aria-label={`Remove ${chip.label} filter`}
          onClick={chip.onRemove}>
          {chip.icon}
          {chip.label}
          <span aria-hidden="true">×</span>
        </Button>
      ))}
      <Button variant="link" size="xs" onClick={onClearAll}>
        Clear all
      </Button>
    </div>
  );
}
